import React, { useState, useEffect } from 'react';
import { createMarkup } from '../../../../../../Foundation/React/code/Utilities/HelperUtilities.js';
import Button from './Button.js';
import { fetchPromise } from './apiHelper.js';

export default (props) => {
  const [returnCompleted, setReturnCompleted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDisclaimerError, setShowDisclaimerError] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [orderLines, setOrderLines] = useState(props.currentOrder.OrderLines);
  const [returnedOrderLines, setReturnedOrderLines] = useState([]);

  const isThule = window._THULEDATA.SitecoreSiteName.toLowerCase() === 'thule';

  //Used for updating orderlines with new values
  const OrderLineKeys = Object.freeze({
    QuantityError: 'QuantityError',
    ReturnReasonError: 'ReturnReasonError',
    QuantityToReturn: 'QuantityToReturn',
    ReturnReason: 'ReturnReason',
  });

  useEffect(() => {
    if (errorMessage) {
      props.scrollToTop();
    }
  }, [errorMessage]);

  const placeReturn = () => {
    setErrorMessage('');
    setIsLoading(true);

    const linesToReturn = orderLines.filter((r) => r.ReturnProduct);
    var returnData = getReturnData(linesToReturn);

    // if return data is not valid
    if (!returnData) {
      setErrorMessage(props.InputValidationMessage);
      setIsLoading(false);
      return;
    }

    // if there are no order lines chosen
    if (returnData.noOrderLinesSelected) {
      setErrorMessage(props.NoLinesSelectedMessage);
      setIsLoading(false);
      return;
    }

    if (!disclaimerAccepted) {
      setErrorMessage(props.ReturnPolicyValidationMessage);
      setShowDisclaimerError(true);
      setIsLoading(false);
      return;
    }

    const url = '/api/sitecore/returns/CreateRMA';
    const options = {
      method: 'POST',
      body: JSON.stringify(returnData),
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
    };

    try {
      fetchPromise(url, options)
        .then((response) => {
          if (response.Success) {
            setReturnedOrderLines(response.Lines);
            sendAnalyticsForReturn(response.Lines);
            setReturnCompleted(true);
            props.scrollToTop();
          } else if (response.Messages.length > 0 && response.Messages[0].Content) {
            setErrorMessage(response.Messages[0].Content);
          } else {
            setErrorMessage(props.GeneralErrorMessage);
          }
        })
        .then(() => {
          setIsLoading(false);
        });
    } catch (err) {
      console.log('error in post request');
      console.log(err);
      setIsLoading(false);
    }
  };

  const getReturnData = (linesToReturn) => {
    const validated = validateLines(linesToReturn);
    if (!validated) return;

    var orderLinesToReturn = linesToReturn.map((t) => ({
      LineId: t.LineId,
      Quantity: t.QuantityToReturn,
    }));

    const reasonsForReturn = linesToReturn.reduce(
      (reasons, currentLine) => reasons + `${currentLine.LineId}:${currentLine.ReturnReason};`,
      ''
    );

    if (orderLinesToReturn && reasonsForReturn) {
      // if orderlines and reason for returns are valid
      var returnData = {
        OrderId: props.currentOrder.OrderId,
        LinesToReturn: orderLinesToReturn,
        ReturnReason: reasonsForReturn,
        ShopName: props.currentOrder.ShopName,
        UserId: props.currentOrder.UserId,
        CustomerId: props.currentOrder.CustomerId,
      };

      return returnData;
    } else if (orderLinesToReturn.length === 0) {
      // if no orderlines are selected
      return { noOrderLinesSelected: true };
    }

    // if orderlines or reason for returns are invalid
    return false;
  };

  const validateLines = (linesToReturn) => {
    let validated = true;
    let allLines = [...orderLines];

    linesToReturn.forEach((ol) => {
      const quantityValidation = validateQuantity(ol.QuantityToReturn);
      const reasonValidation = validateReturnReason(ol.ReturnReason);
      if (!quantityValidation || !reasonValidation) validated = false;

      const returnLineIndex = allLines.findIndex((r) => r.LineId === ol.LineId);
      let line = { ...allLines[returnLineIndex] };

      line.QuantityError = !quantityValidation;
      line.ReturnReasonError = !reasonValidation;
      allLines[returnLineIndex] = line;
    });
    setOrderLines(allLines);
    return validated;
  };

  const updateOrderLines = (input, index) => {
    let lines = [...orderLines];
    let line = { ...lines[index] };

    // Mutates the orderline with the input key and value
    for (let i = 0; i < input.length; i++) {
      line[input[i].key] = input[i].value;
    }

    lines[index] = line;
    return lines;
  };

  const validateQuantity = (quantity) => {
    return !isNaN(quantity);
  };

  const validateReturnReason = (reason) => {
    return !(reason === props.ReasonForReturn) && reason?.length > 0;
  };

  const handleCheckBoxChange = (e, ol, i) => {
    ol.ReturnProduct = e.target.checked;
    if (!e.target.checked) {
      setOrderLines(
        updateOrderLines(
          [
            {
              key: OrderLineKeys.QuantityError,
              value: false,
            },
            {
              key: OrderLineKeys.ReturnReasonError,
              value: false,
            },
          ],
          i
        )
      );
    }
  };

  const handleQuantityChange = (e, i) => {
    setOrderLines(
      updateOrderLines(
        [
          {
            key: OrderLineKeys.QuantityError,
            value: !validateQuantity(e.target.value),
          },
          {
            key: OrderLineKeys.QuantityToReturn,
            value: e.target.value,
          },
        ],
        i
      )
    );
  };

  const handleReturnReasonChange = (e, i) => {
    const updatedLines = updateOrderLines(
      [
        {
          key: OrderLineKeys.ReturnReasonError,
          value: !validateReturnReason(e.target.value),
        },
        {
          key: OrderLineKeys.ReturnReason,
          value: e.target.value,
        },
      ],
      i
    );
    setOrderLines(updatedLines);
  };

  const sendAnalyticsForReturn = (lines) => {
    var orderId = props.currentOrder.OrderId;
    var products = lines.map(function (line) {
      return {
        id: line.Sku,
        quantity: line.Quantity,
      };
    });
    // window.google.tagmanager.events.placeAReturn(orderId, products); //todo fix tagmanager
  };

  const renderOrderInfo = (
    <article className="order-info">
      <p>
        <span className="bold">{props.OrderNumberLabel} </span>
        {props.currentOrder.OrderConfirmationId}
      </p>
      <p>
        <span className="bold">{props.OrderDateLabel} </span>
        {props.currentOrder.OrderDate}
      </p>
    </article>
  );

  const renderItem = (ol, i, isReturned) => {
    return (
      <article key={ol.LineId} className="orderline">
        {!isReturned && (
          <div className="col-xs-12 col-lg-10 col-xl-8 offset-xl-2 offset-lg-1 header-content">
            <div className="col-xs-12 col-md-3">
              <label className="checkbox-container flex-row" htmlFor={`return-product-checkbox-${ol.LineId}`}>
                <input
                  type="checkbox"
                  className="return-item-checkbox"
                  onChange={(e) => handleCheckBoxChange(e, ol, i)}
                  disabled={!ol.IsReturnable}
                  id={`return-product-checkbox-${ol.LineId}`}
                />
                <span className="checkmark"></span>
                <span className="return-item-text">{props.ReturnItem}</span>
                {!ol.IsReturnable && <div className="not-returnable-message">This item is not returnable.</div>}
              </label>
            </div>
            <div className="col-xs-12 col-md-3 col-md-3 offset-md-3 dropdown-container">
              <select
                className={'quantity-to-return' + (ol.QuantityError && ol.ReturnProduct ? ' error' : '')}
                onChange={(e) => handleQuantityChange(e, i)}
                disabled={!ol.IsReturnable}
              >
                <option>{props.QuantityToReturn}</option>
                {Array.from(Array(parseInt(ol.QuantityLeft)).keys()).map((e, i) => {
                  return <option key={i + 1}>{i + 1}</option>;
                })}
              </select>
            </div>
            <div className="col-xs-12 col-md-3 dropdown-container" id="reason-for-return">
              <select
                className={ol.ReturnReasonError && ol.ReturnProduct ? 'error' : ''}
                onChange={(e) => handleReturnReasonChange(e, i)}
                disabled={!ol.IsReturnable}
              >
                <option>{props.ReasonForReturn}</option>
                {Object.entries(props.ReturnReasons).map((key) => (
                  <option key={key[0]} value={key[0]}>
                    {key[1]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className={'col-xs-12 col-lg-10 col-xl-8 offset-xl-2 offset-lg-1 body' + (isReturned ? ' no-header' : '')}>
          <div className="col-xs-6 col-sm-4 product-image">{ol.Image && <img src={ol.Image} alt={ol.ImageAlt || ol.Title} />}</div>

          <div className="col-xs-6 col-sm-8 product-details">
            <div className="column-left">
              <div className="product-name">
                <h5>{ol.Title}</h5>
              </div>
              <div className="product-sku text-small">
                {props.ModelNumber}: <span className="product-sku-value">{isReturned ? ol.Sku : ol.SKUNumber}</span>
              </div>
              <div className="product-quantity text-small">
                {props.Quantity}: <span className="product-quantity-value">{ol.Quantity}</span>
              </div>
            </div>
            <div className="column-right">
              <div className="product-price">
                <span className="text-small">{props.LinePrice}</span>{' '}
                <span className="product-price-value">{isReturned ? ol.Total : ol.LineTotal}</span>
              </div>
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="container returns-container">
      <>
        {returnCompleted ? (
          <div id="return-result">
            <div className="row">
              <div className="col-xs-12 col-lg-10 col-xl-8 offset-xl-2 offset-lg-1">
                <h1 className="headline">{props.ReturnOrderResultHeader}</h1>
                {renderOrderInfo}
              </div>
            </div>

            <section className="row">
              <div
                className="col-xs-12 col-lg-10 col-xl-8 offset-xl-2 offset-lg-1 instructions"
                dangerouslySetInnerHTML={createMarkup(
                  props.showHomeDeliveryDesc
                    ? props.ReturnOrderHomeDeliveryDescription
                    : props.ReturnOrderResultDescription
                )}
              />
            </section>

            {returnedOrderLines?.length > 0 && (
              <>
                <div className="col-xs-12 col-lg-10 col-xl-8 offset-xl-2 offset-lg-1">
                  <h2>{props.ReturnOrderResultItemHeader}</h2>
                </div>
                <section className="orderlines">
                  {returnedOrderLines.map((ol, i) => {
                    return renderItem(ol, i, true);
                  })}
                </section>
              </>
            )}
          </div>
        ) : (
          <div id="return-form">
            <div className="col-lg-8 offset-lg-2">
              {errorMessage && (
                <div className="error-msg-container">
                  <p className="error-msg">{errorMessage}</p>
                  <div className="msg-close" onClick={() => setErrorMessage('')}>
                    <i className="fa fa-times"></i>
                  </div>
                </div>
              )}
            </div>

            <div className="col-xs-12 col-lg-10 col-xl-8 offset-xl-2 offset-lg-1 no-padding-left">
              <h1 className="headline">{props.Heading}</h1>
              {renderOrderInfo}
              <div className="page-intro-text" dangerouslySetInnerHTML={createMarkup(props.ReturnOrderDescription)} />
            </div>

            <section className="orderlines">
              {orderLines.map((ol, i) => {
                return renderItem(ol, i, false);
              })}

              <div className="row new-row-margin">
                <div className="col-xs-12 col-lg-10 col-xl-8 offset-xl-2 offset-lg-1 acknowledgement-container">
                  <div className="col-xs-12 col-sm-8 offset-sm-2 flex">
                    <label className="checkbox-container" htmlFor="disclaimer-checkbox">
                      <input
                        type="checkbox"
                        id="disclaimer-checkbox"
                        onChange={() => setDisclaimerAccepted(!disclaimerAccepted)}
                      />
                      <span className={'checkmark' + (showDisclaimerError ? ' error' : '')}></span>
                      <div dangerouslySetInnerHTML={createMarkup(props.ReturnOrderDisclaimer)} />
                      <div id="gtx-trans" style={{ position: 'absolute', left: '194px', top: '-25.0028px' }}>
                        <div className="gtx-trans-icon">&nbsp;</div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>

              <div className="row new-row-margin">
                <div className="col-xs-12 col-lg-10 col-xl-8 offset-xl-2 offset-lg-1 no-padding-left">
                  <div className="col-xs-12 col-sm-4 offset-sm-4 overflow-hidden">
                    <Button
                      type={'submit'}
                      onClick={(e) => placeReturn(e)}
                      className="thule-btn-default"
                      loading={isLoading}
                      disabled={
                        isLoading || orderLines?.filter((x) => x.ReturnProduct)?.length === 0 || !disclaimerAccepted
                      }
                    >
                      {props.ReturnOrderButton}
                    </Button>
                  </div>
                </div>
              </div>
            </section>
          </div>
        )}
      </>
    </div>
  );
};
